import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ArrowLeftIcon } from 'lucide-react'

import { buttonVariants } from '@ems/ui/components/button'
import { PageHeader } from '@ems/ui/patterns/page-header'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { collectionsQuery } from '#admin/features/cms/queries.ts'
import { isMediaKind, type MediaKind } from '#admin/features/media-library/api.ts'
import { MediaLibraryPage } from '#admin/features/media-library/media-library-page.tsx'
import { libraryPageQuery } from '#admin/features/media-library/queries.ts'
import { MediaLibrarySkeleton } from '#admin/features/media-library/media-library-skeleton.tsx'

/**
 * `/cms/media` — every file uploaded to this edition.
 *
 * A route of its own rather than a tab on Content, the way Floor sits beside Exhibitors: it has
 * its own sidebar entry, and a link to "the PDFs" (`?kind=file`) or a search can be pasted.
 * The literal `media` outranks `content/$id`, so it is never read as a document id.
 *
 * The collections are loaded too, so the usage column can name a document's collection.
 */
export const Route = createFileRoute('/_authenticated/cms/media')({
  pendingComponent: MediaLibrarySkeleton,
  staticData: { crumb: { parents: [{ label: 'CMS' }], label: 'Media' } },
  // Both optional and left out at their defaults, so the sidebar's plain link is the unfiltered
  // page and the address only grows when a filter is actually set. Both keys are always returned,
  // `undefined` when dropped: the router spreads this over the raw URL search, so an omitted key
  // would keep its raw value (`?kind=bogus`) and reach the API.
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string | undefined; kind?: MediaKind | undefined } => ({
    q:
      typeof search['q'] === 'string' && search['q'] !== '' ? search['q'].slice(0, 200) : undefined,
    kind: isMediaKind(search['kind']) && search['kind'] !== 'all' ? search['kind'] : undefined,
  }),
  loaderDeps: ({ search }) => ({ q: search.q ?? '', kind: search.kind ?? 'all' }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureQueryData(sessionQuery()),
      context.queryClient.ensureQueryData(collectionsQuery()),
      context.queryClient.ensureInfiniteQueryData(libraryPageQuery(deps.q, deps.kind)),
    ]),
  component: MediaRoute,
})

function MediaRoute() {
  const { q = '', kind = 'all' } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Media"
        description="Every image and file uploaded to this edition, and where each one is used."
        actions={
          <Link to="/cms/content" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
            <ArrowLeftIcon />
            Content
          </Link>
        }
      />
      <MediaLibraryPage
        q={q}
        kind={kind}
        onSearch={(next) =>
          void navigate({
            // The box searches as the editor types. A new term replaces the address, so Back
            // leaves the search and does not step back one letter. A new kind is a new entry.
            replace: next.kind === kind,
            search: {
              ...(next.q === '' ? {} : { q: next.q }),
              ...(next.kind === 'all' ? {} : { kind: next.kind }),
            },
          })
        }
      />
    </div>
  )
}
